import { useCallback, useEffect, useState } from 'react';
import { useLazySearchFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import { tryDecodeFilePath } from '@/utils/constants';

export const useSharepointUploadConflicts = ({ driveId, itemId, handleUploadFiles }) => {
	const [searchFile] = useLazySearchFoldersAndFilesQuery();

	const [filesQueue, setFilesQueue] = useState([]);
	const [currentFile, setCurrentFile] = useState();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [activeUploadItemId, setActiveUploadItemId] = useState(null);

	const checkFilesExists = useCallback(
		async (filesToSearch, uploadItemId = itemId) => {
			const existing = [];
			const nonExisting = [];

			for (const file of filesToSearch) {
				const { files: search } = await searchFile({
					driveId,
					itemId: uploadItemId,
					searchText: file.name,
					exact: true,
				})
					.unwrap()
					.catch((e) => {
						console.log('checkFilesExists error', e);
						return { files: [] };
					});

				if (
					search.some(
						(f) =>
							tryDecodeFilePath(f?.name, file.name) &&
							f?.parentReference?.id == uploadItemId,
					)
				) {
					existing.push({
						file,
						name: file.name,
					});
				} else {
					nonExisting.push(file);
				}
			}

			return { existing, nonExisting };
		},
		[driveId, itemId, searchFile],
	);

	const processFilesForUpload = useCallback(
		async (newFiles, uploadItemId = itemId) => {
			setUploadLoading(true);
			setActiveUploadItemId(uploadItemId);

			try {
				const { existing, nonExisting } = await checkFilesExists(newFiles, uploadItemId);
				await handleUploadFiles(nonExisting, uploadItemId);
				setFilesQueue(existing);
			} finally {
				setUploadLoading(false);
			}
		},
		[checkFilesExists, handleUploadFiles, itemId],
	);

	const handleCreateCopy = useCallback(async () => {
		if (!currentFile?.file) return;

		setUploadLoading(true);
		try {
			await handleUploadFiles([currentFile.file], activeUploadItemId ?? itemId);
			setDialogOpen(false);
		} finally {
			setUploadLoading(false);
		}
	}, [activeUploadItemId, currentFile, handleUploadFiles, itemId]);

	const handleNextFile = useCallback(() => {
		setDialogOpen(false);
		setCurrentFile(null);
	}, []);

	useEffect(() => {
		if (filesQueue.length > 0 && !dialogOpen) {
			const newQueue = [...filesQueue];
			setCurrentFile(newQueue.pop());
			setFilesQueue(newQueue);
			setDialogOpen(true);
		}
	}, [dialogOpen, filesQueue]);

	return {
		currentFile,
		dialogOpen,
		uploadLoading,
		processFilesForUpload,
		handleCreateCopy,
		handleNextFile,
	};
};
